const GATE_CB_PATTERN = /^\d{13}-[a-fA-F0-9]{6,8}$/;
const GATE_CB_SESSION_KEY = "gate_cb";
const GATE_CB_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function randomHex(length = 6) {
  const size = Math.max(1, Number(length) || 6);
  const bytes = new Uint8Array(Math.ceil(size / 2));
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, size);
}

export function generateGateCb() {
  return `${Date.now()}-${randomHex(6)}`;
}

export function isValidGateCb(value) {
  const cb = Array.isArray(value) ? value[0] : value;
  return GATE_CB_PATTERN.test(String(cb || "").trim());
}

function readCookie(name) {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(/[$()*+.?[\\\]^{|}]/g, "\\$&")}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : "";
  } catch (e) {
    return "";
  }
}

function writeCookie(name, value) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${GATE_CB_COOKIE_MAX_AGE};SameSite=Lax`;
  } catch (e) {
    /* ignore */
  }
}

/** 持久化到 session / local / cookie，方便 APP WebView 冷启动复用 */
export function persistGateCb(cb) {
  if (!isValidGateCb(cb)) return;
  const value = String(cb).trim();
  try {
    sessionStorage.setItem(GATE_CB_SESSION_KEY, value);
  } catch (e) {
    /* ignore */
  }
  try {
    localStorage.setItem(GATE_CB_SESSION_KEY, value);
  } catch (e) {
    /* ignore */
  }
  writeCookie(GATE_CB_SESSION_KEY, value);
}

export function readPersistedGateCb() {
  try {
    const fromSession = sessionStorage.getItem(GATE_CB_SESSION_KEY);
    if (isValidGateCb(fromSession)) return String(fromSession).trim();
  } catch (e) {
    /* ignore */
  }
  try {
    const fromLocal = localStorage.getItem(GATE_CB_SESSION_KEY);
    if (isValidGateCb(fromLocal)) return String(fromLocal).trim();
  } catch (e) {
    /* ignore */
  }
  const fromCookie = readCookie(GATE_CB_SESSION_KEY);
  if (isValidGateCb(fromCookie)) return String(fromCookie).trim();
  return "";
}

/** search 优先，其次 hash 内 query（兼容错误分享链接） */
export function readCbFromLocation(href = window.location.href) {
  try {
    const url = new URL(href);
    const fromSearch = url.searchParams.get("cb");
    if (isValidGateCb(fromSearch)) return String(fromSearch).trim();

    const hash = url.hash || "";
    const qIndex = hash.indexOf("?");
    if (qIndex !== -1) {
      const fromHash = new URLSearchParams(hash.slice(qIndex + 1)).get("cb");
      if (isValidGateCb(fromHash)) return String(fromHash).trim();
    }
  } catch (e) {
    /* ignore */
  }
  return "";
}

export function getOrCreateGateCb() {
  const fromUrl = readCbFromLocation();
  if (fromUrl) {
    persistGateCb(fromUrl);
    return fromUrl;
  }

  const saved = readPersistedGateCb();
  if (saved) {
    persistGateCb(saved);
    return saved;
  }

  const next = generateGateCb();
  persistGateCb(next);
  return next;
}

export function ensureGateCbOnAddressBar(path) {
  if (path === "/kfchat.html" || String(path || "").startsWith("/kfchat")) return;
  if (path === "/app-download" || path === "/app-download/") return;
  if (String(path || "").startsWith("/download/")) return;

  const url = new URL(window.location.href);
  const existingCb = readCbFromLocation(url.href);

  // 地址栏已有合法 cb：只同步持久化，不改写历史栈（保证 $router.back 正常）
  if (isValidGateCb(existingCb)) {
    persistGateCb(existingCb);
    return;
  }

  // 仅用 replaceState 改地址栏，禁止 router.replace，避免顶掉上一页历史
  url.searchParams.set("cb", getOrCreateGateCb());
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
}

/** History 模式邀请注册链接：cb 与 invitation_code 同在 search */
export function buildInviteRegisterUrl(webDomain, invitationCode) {
  const base = String(webDomain || window.location.origin).replace(/\/$/, "");
  const code = String(invitationCode || "").trim();
  if (!code) return "";
  const cb = getOrCreateGateCb();
  const params = new URLSearchParams({ invitation_code: code, cb });
  return `${base}/register?${params.toString()}`;
}

export function getInvitationCodeFromLocation() {
  try {
    return new URL(window.location.href).searchParams.get("invitation_code") || "";
  } catch (e) {
    return "";
  }
}

export { GATE_CB_PATTERN, GATE_CB_SESSION_KEY };
